import React, { useMemo } from 'react'
import { css } from 'linaria'
import { useStoreon } from 'storeon/react'
import { routerKey } from '@storeon/router'
import {
  getCycleText,
  getCycleTextComplete,
} from '@/utils/formatCycle'
import LanguageSelector from '@/components/LanguageSelector'
import Footer from '../../sections/Footer'
import Table from '../../sections/Table'
import Header from '.'
import Loading from '../../pages/Loading'

const styles = css`
  @apply flex flex-col;
  height: 100vh;

  .footer {
    @apply hidden md:block;
  }

  > .content {
    @apply flex flex-col justify-center px-16;
    flex-grow: 1;
    flex-basis: 0;
    overflow-y: scroll;
  }

  @media (max-width: 1023px) {
    height: auto;
    
    > .content {
      @apply px-6 py-8 justify-start;
      flex-grow: 0;
      flex-basis: unset;
    }
  }

  .requirements {
    @apply border-solid border border-gray-300 mt-6 p-8 border-l-0 border-r-0;
    margin-left: 24px;
    margin-right: 24px;

    h3 {
      @apply text-gray-500 font-bold text-base;
    }
    p {
      @apply text-xs mt-2 text-gray-500;
    }
  }

  .language-selector {
    @apply absolute z-10;
    top: 6px;
    right: 20px;

    @screen md {
      top: 80px;
      right: 40px;
    }
  }
`

const shapeProduct = (info, t) => {
  if (info?.paymentLink) {
    const {
      order,
      paymentLink,
    } = info
    const {
      subscriptionInfo, linkImageUrl, linkType,
    } = paymentLink

    if (linkType === 'SUBSCRIPTION' && subscriptionInfo) {
      return {
        name: info.paymentLink.description || '',
        subTotal: info.paymentLink.total || 0,
        billingCyclesNumber: subscriptionInfo.billingCyclesNumber,
        trialCyclesNumber: subscriptionInfo.trialCyclesNumber,
        cyclesToBillBeforeAllowCancelation: subscriptionInfo.cyclesToBillBeforeAllowCancelation,
        itemId: subscriptionInfo.id,
        price: info.paymentLink.total || 0,
        productImageUrl: linkImageUrl,
        quantity: null,
        isSubscription: true,
        billingCycleType: getCycleText(
          subscriptionInfo.billingCycleType, subscriptionInfo.cyclesToBillBeforeAllowCancelation, t, true,
        ),
        trialCycleType: getCycleText(subscriptionInfo.trialCycleType, subscriptionInfo.trialCyclesNumber, t, true),
      }
    }
    return {
      itemId: order?.id,
      name: order?.note || order?.description || paymentLink.description || '',
      price: order?.subTotal || paymentLink.total || 0,
      productImageUrl: order?.orderMetadata?.find(
        (meta) => meta.key === 'LinkImageUrl',
      )?.value || linkImageUrl,
      quantity: 1,
      subTotal: order?.subTotal || paymentLink.total || 0,
      isSubscription: false,
    }
  }

  return {
    itemId: info?.order?.id,
    name: info?.order?.note || info?.order?.description || '',
    price: info?.order?.total || 0,
    productImageUrl: info?.order?.orderMetadata?.find(
      (meta) => meta.key === 'LinkImageUrl',
    )?.value,
    quantity: 1,
    subTotal: info?.order?.subTotal || 0,
    isSubscription: false,
  }
}

const Sidebar = ({ t }) => {
  const {
    dispatch, checkout, link, [routerKey]: route,
  } = useStoreon('checkout', 'link', routerKey)
  const orderId = link?.order?.id || link?.paymentLink?.id

  const onEditCustomData = () => {
    const paymentLinkCode = link?.paymentLink?.id
    if (!paymentLinkCode) return
    const queryStringArray = []
    const customFields = link?.order?.customFields || []
    if (customFields.length) {
      customFields.forEach((field) => queryStringArray.push(`${field.name}=${encodeURIComponent(field.value)}`))
    }
    if (link?.paymentLink?.hasVariableAmount && link?.order?.total) {
      queryStringArray.push(`amount=${encodeURIComponent(link.order.total)}`)
    }
    window.location = `/pl/${paymentLinkCode}?${queryStringArray.join('&')}`
  }

  const orderType = link?.order?.orderType

  const isntWuhu = route?.match?.page !== 'wuhu'

  let products = []
  let ship = {
    price: 0,
  }

  if (orderType === 'FULL' || orderType === 'STOREFRONT') {
    products = [...(link?.order?.orderDetails || [])]
  } else if (link) {
    products.push(shapeProduct(link, t))
  }

  const shipmentOptionId = isntWuhu ? checkout?.shipmentOptionId : link?.order?.shipmentOptionId

  const shipmentOptions = link?.shipmentOptions || []
  const selectedShipmentOption = useMemo(() => {
    if (!shipmentOptions.length) return null

    const primaryOption = shipmentOptions.find((o) => o.shipmentOptionId === shipmentOptionId)

    if (isntWuhu) {
      // On the live checkout, only show the delivery line once the buyer has
      // actually selected a shipping method — no fallback to a default option,
      // otherwise it shows a fee (and a price the total doesn't yet include)
      // before any choice, and the option isn't always C807xpress.
      return primaryOption ?? null
    }

    return primaryOption
      ?? shipmentOptions[0]
      ?? null
  }, [shipmentOptionId, shipmentOptions, isntWuhu])

  if (selectedShipmentOption) {
    const option = selectedShipmentOption

    const shipmentPrice = option.requiresQuote
      ? (link?.effects?.deliveryCost ?? 0)
      : (option.baseCost ?? 0)

    ship = {
      itemId: 0,
      name: `${t`link.deliveryTitle`}: ${option?.name || ''}`,
      price: shipmentPrice,
      productImageUrl: null,
      quantity: 1,
      subTotal: shipmentPrice,
    }
  }

  if (link?.paymentLink?.linkType !== 'SUBSCRIPTION' && typeof ship.price === 'number' && ship.price > 0) {
    products.push(ship)
  }

  const tipTotal = isntWuhu ? (link?.effects?.sessionDetail?.tip ?? 0) : (link?.order?.tip ?? 0)
  if (tipTotal > 0) {
    const tipOption = (link?.effects?.tipOptions || []).find(({ amount }) => amount === tipTotal)
    products.push({
      itemId: 1,
      name: `${t`link.tip`} (${tipOption?.percentageLabel || ''})`,
      price: tipTotal,
      productImageUrl: null,
      quantity: 1,
      subTotal: tipTotal,
    })
  }

  const orderSubTotal = link?.order?.subTotal ?? 0
  const shipPriceForTotal = isntWuhu ? (ship.price || 0) : 0
  const orderTotal = link?.effects?.sessionDetail?.total || (orderSubTotal + shipPriceForTotal + tipTotal)

  if (link?.paymentLink?.linkType !== 'SUBSCRIPTION' && !link?.order?.id) {
    return <Loading />
  }

  return (
    <div className={styles}>
      <div className="content">
        <div className="language-selector">
          <LanguageSelector />
        </div>
        <Header
          storeImageUrl={link?.store?.storeImageUrl}
          storeName={link?.store?.name || ''}
          orderId={String(orderId || '')}
          orderType={t`link.orderTitle`}
          storeUrl={link?.store?.storeUrl}
        />
        {
          link?.order?.hasVariableAmount ? (
            <Table
              products={products}
              hasVariableAmount
              canEditCustomData={false}
              canEditCustomDataFn={onEditCustomData}
              orderNotes={checkout?.checkoutNote}
              onChangeOrderNotes={(value) => dispatch('checkout/save', { checkoutNote: value })}
              showNotes={isntWuhu}
            />
          ) : (
            <>
              {
                link?.paymentLink?.linkType === 'SUBSCRIPTION' ? (
                  <Table
                    products={products}
                    total={parseFloat(link.paymentLink.total || 0, 10)}
                    currencyLocale={link.store?.config?.locale}
                    currencyCode={link.store?.config?.currencyCode}
                    hasVariableAmount={false}
                    customFields={[]}
                    canEditCustomData={false}
                    orderNotes={checkout?.checkoutNote}
                    onChangeOrderNotes={(value) => dispatch('checkout/save', { checkoutNote: value })}
                    totalSubtext={getCycleTextComplete(
                      link.paymentLink?.subscriptionInfo?.billingCycleType,
                      link.paymentLink?.subscriptionInfo?.billingCyclesNumber,
                      t,
                      true,
                    )}
                    showNotes={isntWuhu}
                  />
                ) : (
                  <>
                    <Table
                      products={products}
                      total={parseFloat(orderTotal)}
                      hasVariableAmount={false}
                      customFieldsDesc={link?.paymentLink?.customFields}
                      customFields={link?.order?.customFields}
                      canEditCustomData={
                        (link?.order?.customFields?.length || 0) > 0
                        && link?.paymentLink?.customFields?.some((f) => f.isEditable)
                      }
                      canEditCustomDataFn={onEditCustomData}
                      orderNotes={checkout?.checkoutNote}
                      onChangeOrderNotes={(value) => dispatch('checkout/save', { checkoutNote: value })}
                      showNotes={isntWuhu && route?.match?.page !== 'status'}
                      discounts={isntWuhu ? link?.effects?.discounts : link?.order?.discountEffects}
                      surcharges={isntWuhu ? link?.effects?.surcharges : link?.order?.surchargeEffects}
                    />
                  </>
                )
              }
            </>
          )
        }

        {
          link?.paymentLink?.subscriptionInfo?.termsAndConditions && (
            <div className="requirements">
              <h3>{t`link.requirementsTitle`}</h3>
              <p>{link.paymentLink.subscriptionInfo.termsAndConditions}</p>
            </div>
          )
        }
      </div>
      <div className="footer">
        <Footer />
      </div>
    </div>
  )
}

export default Sidebar
